import { useFormik } from "formik";
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Empty } from "antd";
import { useTranslation } from "react-i18next";

import apiClient from "../../../../helpers/apiClient";
import { useToast } from "../../../../contexts/ToastContext";

function Comments({ data }) {
    const { t } = useTranslation("customers");
    const toast = useToast();

    const [datas, setDatas] = useState([]);
    const [isCommentSubmit, setIsCommentSubmit] = useState(false);

    useEffect(() => {
        if (!data) {
            setDatas([]);
            return () => {};
        }

        let cancelled = false;
        (async () => {
            try {
                const commentAddResult = await apiClient.get(`/api/user/getComments/${data}`);
                if (!cancelled) {
                    setDatas(commentAddResult.data?.data ?? []);
                }
            } catch {
                if (!cancelled) {
                    setDatas([]);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [data]);

    const formik = useFormik({
        initialValues: {
            comment: "",
        },
        onSubmit: async (values, { resetForm }) => {
            if (!data) {
                return;
            }

            setIsCommentSubmit(true);
            try {
                const commentAddResult = await apiClient.post(`/api/user/addComment/${data}`, {
                    comment: values.comment,
                });

                if (commentAddResult.data?.success === true) {
                    setDatas((prev) => [...prev, values.comment]);
                    resetForm();
                    toast.success({ message: t("comments.saveSuccess") });
                } else {
                    toast.error({ message: t("comments.saveError") });
                }
            } catch {
                toast.error({ message: t("comments.saveError") });
            } finally {
                setIsCommentSubmit(false);
            }
        },
    });

    return (
        <div className="relative h-[320px] fontRegular mt-3">
            <div className="overflow-y-auto h-[calc(100%-6rem)]">
                {datas.length === 0 && <Empty className="mt-14" description={t("comments.noComments")} />}
                <ul className="space-y-2">
                    {datas.map((elem, index) => (
                        <li key={`${elem}-${index}`}>
                            <div className="bg-white py-2 px-3 text-sm rounded-3xl">{elem}</div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className="absolute bottom-0 w-full">
                <form onSubmit={formik.handleSubmit}>
                    <input
                        className="mx-auto w-[98%] h-12 outline-none bg-[#f2f2fa] border-b border-dotted border-[#acacb9] mb-1.5 font-poppins font-light text-[0.9rem] break-words"
                        type="text"
                        name="comment"
                        autoComplete="off"
                        placeholder={t("comments.typeHere")}
                        onChange={formik.handleChange}
                        value={formik.values.comment}
                        disabled={isCommentSubmit || !data}
                    />
                    <div className="flex justify-end">
                        <Button
                            type="primary"
                            shape="round"
                            htmlType="submit"
                            loading={isCommentSubmit}
                            disabled={!data}>
                            {t("comments.addComment")}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}

Comments.propTypes = {
    data: PropTypes.string.isRequired,
};

export default Comments;
